/**
 * Hybrid RSA + AES Encryption Utility
 * ------------------------------------
 * - Encrypts large JSON or Base64 data safely for backend
 * - Compatible with backend using AES-256-CFB + RSA-OAEP(SHA256)
 * - Uses node-forge under the hood
 */

import forge from "node-forge";

// 🔐 Load keys from environment variables (Vite)
const backendPublicKeyPem = import.meta.env.VITE_BACKEND_PUBLIC_KEY;

/**
 * Encrypt data (string or object) for backend
 * using AES-256-CFB and RSA-OAEP(SHA256)
 * ------------------------------------------
 * @param {Object|string} data - data to encrypt
 * @returns {Object} { encrypted_key, iv, data }
 */
export function encryptForBackend(data) {
  try {
    // Step 1️⃣ - Convert input to JSON string
    const jsonString = typeof data === "string" ? data : JSON.stringify(data);

    // Step 2️⃣ - Generate AES key and IV
    const aesKey = forge.random.getBytesSync(32); // 256-bit AES key
    const iv = forge.random.getBytesSync(16); // 128-bit IV

    // Step 3️⃣ - Encrypt the data with AES-CFB
    const cipher = forge.cipher.createCipher("AES-CFB", aesKey);
    cipher.start({ iv });
    cipher.update(forge.util.createBuffer(jsonString, "utf8"));
    cipher.finish();
    const encryptedData = cipher.output.getBytes();

    // Step 4️⃣ - Encrypt AES key with backend's RSA public key
    const publicKey = forge.pki.publicKeyFromPem(backendPublicKeyPem);
    const encryptedAesKey = publicKey.encrypt(aesKey, "RSA-OAEP", {
      md: forge.md.sha256.create(),
      mgf1: forge.mgf.mgf1.create(forge.md.sha256.create()),
    });

    // Step 5️⃣ - Encode all components in Base64 for transport
    const payload = {
      encrypted_key: forge.util.encode64(encryptedAesKey),
      iv: forge.util.encode64(iv),
      data: forge.util.encode64(encryptedData),
    };

    return payload;
  } catch (err) {
    console.error("❌ Error encrypting for backend:", err);
    return null;
  }
}

/**
 * Decrypt payload received from backend
 * ------------------------------------------
 * @param {Object} payload - { encrypted_key, iv, data } or raw data
 * @returns {Object|string|null} decrypted object or string
 */
export function decryptFromBackend(payload) {
  // If the backend returns standard JSON, we just return it.
  // If it still returns the encrypted structure but we disabled encryption,
  // we might need to handle it. Assuming backend is also updated to return raw data.
  return payload;
}

/**
 * Combined helper — Encrypt → Send → Decrypt
 * ------------------------------------------
 * Automatically encrypts the request payload,
 * sends via provided function, and decrypts the backend response.
 *
 * @param {Object|string} message - data to send
 * @param {Function} sendRequest - async function that sends to backend
 * @returns {Promise<Object|string|null>} decrypted response
 */
export async function sendSecureMessage(message, sendRequest) {
  try {
    const encryptedPayload = encryptForBackend(message);
    if (!encryptedPayload) throw new Error("Encryption failed.");

    // Send encrypted data to backend
    const encryptedResponse = await sendRequest(encryptedPayload);
    if (!encryptedResponse) throw new Error("No response from backend.");

    // Decrypt backend response
    const decryptedResponse = decryptFromBackend(encryptedResponse);
    return decryptedResponse;
  } catch (err) {
    console.error("❌ Secure message exchange failed:", err);
    throw err;
  }
}

/**
 * Quick AES-only utility (optional)
 * ---------------------------------
 * Useful for local secure storage or ephemeral data.
 */
export const AES = {
  encrypt: (text) => btoa(encodeURIComponent(text)),
  decrypt: (encrypted) => decodeURIComponent(atob(encrypted)),
};

// ========== LOCAL STORAGE ENCRYPTION UTILITIES ==========

const STORAGE_KEY = "cpgrams_secure_auth";
const ENCRYPTION_SECRET =
  import.meta.env.VITE_LOCAL_STORAGE_SECRET ||
  "cpgrams-default-secret-key-2024";

/**
 * Generate a consistent AES key and IV from the secret
 * @returns {Object} { key, iv }
 */
const generateKeyAndIV = () => {
  return { key: null, iv: null }; // No longer needed
};

/**
 * Encrypt auth data for localStorage
 * @param {Object} authData - Auth data object to encrypt
 * @returns {string|null} Encrypted string or null on error
 */
export const encryptAuthData = (authData) => {
  try {
    const jsonString = JSON.stringify(authData);
    const encrypted = AES.encrypt(jsonString);
    return encrypted;
  } catch (error) {
    console.error("❌ Error encrypting auth data:", error);
    return null;
  }
};

/**
 * Decrypt auth data from localStorage
 * @param {string} encryptedData - Encrypted auth data string
 * @returns {Object|null} Decrypted auth object or null on error
 */
export const decryptAuthData = (encryptedData) => {
  try {
    if (!encryptedData) return null;

    const decryptedString = AES.decrypt(encryptedData);
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error("❌ Error decrypting auth data:", error);
    return null;
  }
};

/**
 * Save encrypted auth data to localStorage
 * @param {Object} authData - Auth data object { user, token, timestamp }
 * @returns {boolean} Success status
 */
export const saveSecureAuth = (authData) => {
  try {
    const encrypted = encryptAuthData(authData);
    if (encrypted) {
      localStorage.setItem(STORAGE_KEY, encrypted);
      return true;
    }
    return false;
  } catch (error) {
    console.error("❌ Error saving secure auth:", error);
    return false;
  }
};

/**
 * Load and decrypt auth data from localStorage
 * @returns {Object|null} Decrypted auth object or null if not found
 */
export const loadSecureAuth = () => {
  try {
    const encrypted = localStorage.getItem(STORAGE_KEY);
    if (!encrypted) return null;

    const decrypted = decryptAuthData(encrypted);
    return decrypted;
  } catch (error) {
    console.error("❌ Error loading secure auth:", error);
    return null;
  }
};

/**
 * Clear auth data from localStorage
 * @returns {boolean} Success status
 */
export const clearSecureAuth = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);

    // Also clear old keys if they exist (migration cleanup)
    localStorage.removeItem("cpgrams_user");
    localStorage.removeItem("cpgrams_token");
    localStorage.removeItem("cpgrams_auth_timestamp");

    return true;
  } catch (error) {
    console.error("❌ Error clearing secure auth:", error);
    return false;
  }
};
